import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { Box, Grid, SxProps } from '@mui/material';
import { colors } from '../utils/theme';
import { Theme } from '@mui/material/styles';

interface RootineCarouselProps {
  children: ReactNode[];
  itemsToShow?: number;
  className?: string;
  boxStyle?: SxProps<Theme>;
  activeDotColor?: string;
  inactiveDotColor?: string;
  centerAlign?: boolean;
}

export const RootineCarousel: React.FC<RootineCarouselProps> = ({
  children,
  itemsToShow = 1,
  className,
  boxStyle,
  activeDotColor,
  inactiveDotColor,
  centerAlign = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startX, setStartX] = useState<number | null>(null);
  const carouselRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (currentIndex > children.length - itemsToShow) {
      setCurrentIndex(children.length - itemsToShow);
    }
  }, [children, currentIndex, itemsToShow]);

  const nextSlide = () => {
    setCurrentIndex((prev) =>
      prev + itemsToShow < children.length ? prev + itemsToShow : prev
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) =>
      prev - itemsToShow >= 0 ? prev - itemsToShow : 0
    );
  };

  const isTouchEvent = (e: MouseEvent | TouchEvent): e is TouchEvent =>
    (e as TouchEvent).changedTouches !== undefined;

  // `handleSwipeStart` begins tracking a swipe event.
  // It detects whether the event is a touch or mouse event,
  // then records the x-coordinate where the swipe started.
  const handleSwipeStart = (e: React.MouseEvent | React.TouchEvent) => {
    const xPos = isTouchEvent(e.nativeEvent)
      ? e.nativeEvent.touches[0].pageX // Get x-coordinate of the touch point.
      : e.nativeEvent.pageX; // Get x-coordinate of the mouse pointer.
    setStartX(xPos);
  };

  // `handleSwipeEnd` completes the tracking of a swipe event.
  // It calculates the x-coordinate difference from start to end.
  // If the swipe distance is large enough (over 100px),
  // it determines swipe direction (left or right) and changes slide.
  const handleSwipeEnd = (e: React.MouseEvent | React.TouchEvent) => {
    const endX = isTouchEvent(e.nativeEvent)
      ? e.nativeEvent.changedTouches[0].pageX
      : e.nativeEvent.pageX;
    if (startX === null) {
      return;
    }

    // Difference in x-coord from start to end.
    const diffX = endX - startX;

    // Determine swipe direction and change slide if swipe was large enough.
    if (Math.abs(diffX) > 100) {
      if (diffX > 0) {
        prevSlide();
      } else {
        nextSlide();
      }
    }
  };

  return (
    <div
      className={className}
      ref={carouselRef}
      onMouseDown={handleSwipeStart}
      onMouseUp={handleSwipeEnd}
      onTouchStart={handleSwipeStart}
      onTouchEnd={handleSwipeEnd}
    >
      <Grid container flexDirection="row" sx={{ zIndex: 1 }}>
        {children.slice(currentIndex, currentIndex + itemsToShow)}
      </Grid>

      <Box
        display="flex"
        justifyContent={centerAlign ? 'center' : 'flex-start'}
        sx={boxStyle}
      >
        {children.map((_, index) => (
          <Box
            key={index}
            mx={1}
            width={currentIndex === index ? 40 : 10}
            height={10}
            borderRadius={50}
            bgcolor={
              currentIndex === index
                ? activeDotColor || colors.greys.BRAND_BLACK
                : inactiveDotColor || colors.greys.GREY_400
            }
            onClick={() => setCurrentIndex(index)}
            style={{ cursor: 'pointer', zIndex: 2 }}
          />
        ))}
      </Box>
    </div>
  );
};
